import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  Progress,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconDeviceFloppy, IconInfoCircle, IconSparkles, IconUsers } from '@tabler/icons-react'
import { useCallback, useMemo, useState } from 'react'

import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import * as commonClasses from '@/styles/componentCommons.css'
import { collectSpeakers, formatTime, parseTranscriptSegments, speakerNumberFromLabel } from '@/utils/transcript'

const SPEAKER_COLORS = ['indigo', 'teal', 'grape', 'orange', 'cyan', 'pink', 'lime', 'violet'] as const

const shade = (color: string, level: number) => `var(--mantine-color-${color}-${level})`

export function IdentifySpeakersModal(props: CMFormSection) {
  const { api } = useGlobalState()

  const originalTranscript = props.stepContentQuery.data?.content ?? ''
  const metadata = props.stepContentQuery.data?.speakerNamesMetadata ?? null

  const segments = useMemo(() => parseTranscriptSegments(originalTranscript), [originalTranscript])
  const detected = useMemo(() => collectSpeakers(originalTranscript, metadata), [originalTranscript, metadata])
  const speakers = useMemo(() => detected.map(speaker => speaker.label), [detected])

  const speakerIndex = useMemo(() => {
    const map: Record<string, number> = {}
    speakers.forEach((label, i) => {
      map[label] = i
    })
    return map
  }, [speakers])

  const colorFor = useCallback(
    (label: string) => SPEAKER_COLORS[(speakerIndex[label] ?? 0) % SPEAKER_COLORS.length],
    [speakerIndex],
  )

  // Speaking time is approximated as the delta to the next segment's start.
  const statsBySpeaker = useMemo(() => {
    const map: Record<string, { count: number; seconds: number; samples: string[] }> = {}
    for (const label of speakers) map[label] = { count: 0, seconds: 0, samples: [] }
    segments.forEach((seg, i) => {
      const entry = map[seg.label]
      if (!entry) return
      entry.count += 1
      const next = segments[i + 1]
      if (seg.timeSec != null && next?.timeSec != null) {
        entry.seconds += Math.max(next.timeSec - seg.timeSec, 1)
      }
      if (entry.samples.length < 2 && seg.text) entry.samples.push(seg.text)
    })
    return map
  }, [segments, speakers])

  // AI guesses per label; seeded from metadata, refreshed by re-suggest.
  const [suggested, setSuggested] = useState<Record<string, string | null>>(() => {
    const map: Record<string, string | null> = {}
    if (metadata) for (const [label, entry] of Object.entries(metadata)) map[label] = entry.suggested
    return map
  })
  // Editable name per speaker, seeded from whatever was detected at processing time —
  // those names are pre-accepted. Guesses produced by a later "Suggest with AI" land in
  // `suggested` only and stay as chips until the expert applies one.
  const [drafts, setDrafts] = useState<Record<string, string>>(() => {
    const map: Record<string, string> = {}
    for (const speaker of detected) map[speaker.label] = speaker.name ?? ''
    return map
  })
  const [hint, setHint] = useState('')
  const [isSuggesting, setIsSuggesting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const hasAnySuggestion = speakers.some(label => suggested[label]?.trim())
  // Re-suggesting without new guidance just regenerates the same guesses, so a hint is required.
  const reSuggestDisabled = hasAnySuggestion && !hint.trim()
  const namedCount = speakers.filter(label => drafts[label]?.trim()).length
  const progress = speakers.length ? (namedCount / speakers.length) * 100 : 0
  const allNamed = speakers.length > 0 && namedCount === speakers.length

  // Detected-or-typed name, else a generic label. A fresh AI suggestion is never shown
  // as the name until the expert applies it (clicks the chip or types).
  // Generic label uses the `spk_N` number so it matches the "(speaker N)" shown
  // in TranscriptView (preview / full-content views).
  const nameFor = useCallback(
    (label: string) => drafts[label]?.trim() || `Speaker ${speakerNumberFromLabel(label)}`,
    [drafts],
  )

  const setDraft = useCallback((label: string, value: string) => {
    setDrafts(prev => ({ ...prev, [label]: value }))
  }, [])

  const acceptSuggestion = useCallback(
    (label: string) => {
      const value = suggested[label]?.trim()
      if (value) setDraft(label, value)
    },
    [suggested, setDraft],
  )

  // Refresh the suggestion chips only; the expert still clicks to apply one.
  const reSuggest = useCallback(async () => {
    if (!originalTranscript) return
    setIsSuggesting(true)
    try {
      const response = await api.suggestSpeakerNames({
        step_id: props.stepId ?? undefined,
        transcript: originalTranscript,
        hint: hint.trim() || undefined,
      })
      setSuggested(prev => ({ ...prev, ...(response.speakerNames ?? {}) }))
    } catch (error) {
      console.error('Suggest speakers failed:', error)
      notifications.show({
        title: 'Could not suggest names',
        message: 'There was a problem suggesting speaker names. Enter them manually or try again.',
        color: 'red',
        position: 'bottom-left',
      })
    } finally {
      setIsSuggesting(false)
    }
  }, [api, originalTranscript, props.stepId, hint])

  // Apply names to the transcript and persist text + metadata. Repeatable, since
  // identity lives in metadata rather than the text.
  const handleSave = useCallback(async () => {
    if (!originalTranscript || !props.stepId) return
    // Only applied names, never bare suggestions. An empty name is sent as '' because the
    // backend keeps the previous name for any label left out (C2-2539).
    const speakerNames: Record<string, string> = {}
    for (const label of speakers) speakerNames[label] = drafts[label]?.trim() ?? ''

    setIsSaving(true)
    try {
      const response = await api.applySpeakerNames({
        step_id: props.stepId,
        transcript: originalTranscript,
        speaker_names: speakerNames,
      })
      await props.contentUpdateMutation.mutateAsync({
        step_id: props.stepId,
        text: response.transcript,
        speaker_names_metadata: JSON.stringify(response.speakerNamesMetadata),
      })
      notifications.show({
        title: 'Speakers updated',
        message: 'Speaker names were applied to the transcript.',
        color: 'teal',
        position: 'bottom-left',
      })
      props.closeModal()
    } catch (error) {
      console.error('Failed to apply and save transcript:', error)
      notifications.show({
        title: 'Could not save transcript',
        message: 'There was a problem applying speaker names. Please try again.',
        color: 'red',
        position: 'bottom-left',
      })
    } finally {
      setIsSaving(false)
    }
  }, [api, originalTranscript, props.stepId, props.contentUpdateMutation, props.closeModal, speakers, drafts])

  if (speakers.length === 0) {
    return (
      <>
        <Box className={commonClasses.stickyModalFooterContentWrapper}>
          <Alert color="gray" variant="light" icon={<IconInfoCircle size={18} />}>
            No speaker labels were found in this transcript. Speaker identification is only available for transcripts
            that contain speaker markers like{' '}
            <Text span ff="monospace">
              spk_0
            </Text>
            .
          </Alert>
        </Box>
        <Modal.Header className={commonClasses.stickyModalFooter}>
          <Group justify="flex-end" w="100%">
            <Button size="sm" variant="light" color="gray" onClick={() => props.closeModal()}>
              Close
            </Button>
          </Group>
        </Modal.Header>
      </>
    )
  }

  return (
    <>
      <Box className={commonClasses.stickyModalFooterContentWrapper}>
        <Group align="stretch" gap="lg" wrap="nowrap" style={{ minHeight: '60vh' }}>
          <Stack gap="sm" style={{ flex: 1.15, minWidth: 0 }}>
            <Text fw={600}>Transcript</Text>

            <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
              <ScrollArea h="56vh" type="auto">
                <Stack gap={0} p="xs">
                  {segments.map(seg => {
                    const color = colorFor(seg.label)
                    return (
                      <Box key={seg.id} p="sm" style={{ borderRadius: theme.radius.md }}>
                        <Group gap="xs" mb={4} wrap="nowrap">
                          <Text size="xs" c="dimmed" ff="monospace" style={{ flexShrink: 0 }}>
                            {formatTime(seg.timeSec)}
                          </Text>
                          <Badge
                            size="sm"
                            radius="sm"
                            variant="light"
                            color={color}
                            leftSection={
                              <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: shade(color, 6) }} />
                            }>
                            {nameFor(seg.label)}
                          </Badge>
                        </Group>
                        <Text size="sm" c="gray.8" pl={2}>
                          {seg.text}
                        </Text>
                      </Box>
                    )
                  })}
                </Stack>
              </ScrollArea>
            </Paper>
          </Stack>

          <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
            <Group justify="space-between" align="center">
              <Group gap={6}>
                <IconUsers size={18} />
                <Text fw={600}>Detected speakers</Text>
              </Group>
              <Text size="sm" c={allNamed ? 'teal.7' : 'dimmed'} fw={500}>
                {namedCount} of {speakers.length} named
              </Text>
            </Group>

            <Progress
              value={progress}
              color={allNamed ? 'teal' : 'indigo'}
              radius="xl"
              size="sm"
              transitionDuration={200}
            />

            <Group gap="sm" wrap="nowrap" align="flex-end">
              <TextInput
                style={{ flex: 1 }}
                size="sm"
                radius="md"
                label="Hint"
                placeholder="e.g. the host is John, the guest is Dr. Chen"
                value={hint}
                onChange={e => setHint(e.currentTarget.value)}
              />
              <Tooltip
                label={
                  reSuggestDisabled ? 'Add a hint to re-suggest names' : 'Suggest a name for every speaker using AI'
                }>
                <Button
                  size="sm"
                  radius="md"
                  variant="light"
                  leftSection={<IconSparkles size={16} />}
                  loading={isSuggesting}
                  disabled={reSuggestDisabled}
                  onClick={reSuggest}>
                  {hasAnySuggestion ? 'Re-suggest' : 'Suggest with AI'}
                </Button>
              </Tooltip>
            </Group>

            <ScrollArea h="44vh" type="auto" offsetScrollbars>
              <Stack gap="sm" pr="xs">
                {speakers.map(label => {
                  const stats = statsBySpeaker[label]
                  const draft = drafts[label]?.trim() ?? ''
                  const suggestion = suggested[label]?.trim() ?? ''
                  const isNamed = !!draft
                  const color = colorFor(label)
                  const showChip = !!suggestion && suggestion !== draft
                  return (
                    <Paper key={label} withBorder radius="md" p="md">
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                          <Avatar color={color} radius="xl">
                            {isNamed ? (
                              draft.charAt(0).toUpperCase()
                            ) : (
                              <Text fw={700}>{speakerNumberFromLabel(label)}</Text>
                            )}
                          </Avatar>
                          <Stack gap={0} style={{ minWidth: 0 }}>
                            <Group gap={6}>
                              <Text fw={600} size="sm">
                                {nameFor(label)}
                              </Text>
                              {isNamed && (
                                <Badge
                                  size="xs"
                                  radius="sm"
                                  color="teal"
                                  variant="light"
                                  leftSection={<IconCheck size={11} />}>
                                  Named
                                </Badge>
                              )}
                            </Group>
                            <Text size="xs" c="dimmed">
                              {stats.count} segments
                              {stats.seconds > 0 ? ` · ${formatTime(stats.seconds)} speaking` : ''}
                            </Text>
                          </Stack>
                        </Group>
                      </Group>

                      <Stack gap={4} mt="sm">
                        {stats.samples.map((sample, i) => (
                          <Text key={i} size="xs" c="gray.6" lineClamp={2} fs="italic">
                            &ldquo;{sample}&rdquo;
                          </Text>
                        ))}
                      </Stack>

                      <TextInput
                        mt="sm"
                        radius="md"
                        placeholder="Type a name…"
                        value={drafts[label] ?? ''}
                        onChange={e => setDraft(label, e.currentTarget.value)}
                        rightSection={isNamed ? <IconCheck size={16} color={shade('teal', 6)} /> : null}
                      />

                      {showChip && (
                        <Group gap={8} mt={8} align="center">
                          <Group gap={4} align="center" style={{ flexShrink: 0 }}>
                            <IconSparkles size={14} color={shade('indigo', 5)} />
                            <Text size="xs" c="dimmed">
                              Suggested:
                            </Text>
                          </Group>
                          <Tooltip label="Use this suggestion">
                            <Badge
                              component="button"
                              type="button"
                              size="lg"
                              radius="sm"
                              variant="outline"
                              color="indigo"
                              style={{ cursor: 'pointer' }}
                              onClick={() => acceptSuggestion(label)}>
                              {suggestion}
                            </Badge>
                          </Tooltip>
                        </Group>
                      )}
                    </Paper>
                  )
                })}
              </Stack>
            </ScrollArea>
          </Stack>
        </Group>
      </Box>

      <Modal.Header className={commonClasses.stickyModalFooter}>
        <Group justify="space-between" w="100%">
          <Text size="sm" c="dimmed">
            {allNamed
              ? 'All speakers named.'
              : `${speakers.length - namedCount} speaker(s) still need a name (optional).`}
          </Text>
          <Group gap="md">
            <Button size="sm" variant="light" color="gray" disabled={isSaving} onClick={() => props.closeModal()}>
              Cancel
            </Button>
            <Button size="sm" leftSection={<IconDeviceFloppy size={18} />} loading={isSaving} onClick={handleSave}>
              Save
            </Button>
          </Group>
        </Group>
      </Modal.Header>
    </>
  )
}
